import { Card } from '../Card';
import { Button } from '../Button';
import { StatusBadge } from './StatusBadge';
import type { UserSubscription } from '../../types';

interface CurrentPlanCardProps {
  subscription: UserSubscription;
  onUpgrade: () => void;
  onManagePayment: () => void;
  onCancel: () => void;
  isLoadingPortal?: boolean;
}

export function CurrentPlanCard({
  subscription,
  onUpgrade,
  onManagePayment,
  onCancel,
  isLoadingPortal = false,
}: CurrentPlanCardProps) {
  const isDeveloper = subscription.tier === 'developer';
  const isPastDue = subscription.status === 'past_due';
  const isCanceled = subscription.status === 'canceled';

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Card title="Current Plan">
      <div className="space-y-6">
        {/* Status Warning Banners */}
        {isPastDue && (
          <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-warning text-xl">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-warning">Payment Required</h3>
                <div className="mt-1 text-sm text-warning/80">
                  Your payment method failed. Please update your payment information to restore full access.
                </div>
                <div className="mt-3">
                  <Button size="sm" onClick={onManagePayment} isLoading={isLoadingPortal}>
                    Update Payment Method
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isCanceled && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-destructive text-xl">ℹ️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">Subscription Canceled</h3>
                <div className="mt-1 text-sm text-destructive/80">
                  Your subscription has been canceled. You have read-only access until{' '}
                  {formatDate(subscription.current_period.end)}.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plan Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground mb-1">Tier</dt>
            <dd className="flex items-center gap-2">
              <StatusBadge type="tier" value={subscription.tier} />
              {subscription.tier === 'developer' && (
                <span className="text-xs text-muted-foreground">(Free)</span>
              )}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-muted-foreground mb-1">Status</dt>
            <dd>
              <StatusBadge type="status" value={subscription.status} />
            </dd>
          </div>

          {!isDeveloper && (
            <>
              <div>
                <dt className="text-sm font-medium text-muted-foreground mb-1">Billing Cycle</dt>
                <dd className="text-sm text-foreground capitalize">{subscription.billing_cycle}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-muted-foreground mb-1">Currency</dt>
                <dd className="text-sm text-foreground uppercase">{subscription.currency}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-muted-foreground mb-1">Current Period</dt>
                <dd className="text-sm text-foreground">
                  {formatDate(subscription.current_period.start)} -{' '}
                  {formatDate(subscription.current_period.end)}
                </dd>
              </div>

              {subscription.current_period.next_billed_at && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">Next Billing Date</dt>
                  <dd className="text-sm text-foreground">
                    {formatDate(subscription.current_period.next_billed_at)}
                  </dd>
                </div>
              )}
            </>
          )}
        </div>

        {/* Plan Limits */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Plan Limits</h4>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Protected Identities</dt>
              <dd className="font-medium text-foreground">
                {subscription.limits.identities_max.toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Custom Contexts</dt>
              <dd className="font-medium text-foreground">
                {subscription.limits.contexts_max}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Data Retention</dt>
              <dd className="font-medium text-foreground">
                {subscription.limits.data_retention_days} days
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Bulk Operations</dt>
              <dd className="font-medium text-foreground capitalize">
                {subscription.limits.bulk_operations}
              </dd>
            </div>
          </dl>
        </div>

        {/* Plan Features */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Features</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {isDeveloper ? (
              <div className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span className="text-foreground">Evaluate all base paid plan features</span>
              </div>
            ) : (
              Object.entries(subscription.features).map(([key, value]) => {
                if (typeof value === 'boolean') {
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className={value ? 'text-success' : 'text-muted-foreground'}>
                        {value ? '✓' : '✗'}
                      </span>
                      <span className="text-foreground capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                }
                if (value && typeof value === 'string') {
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-success">✓</span>
                      <span className="text-foreground capitalize">
                        {key.replace(/_/g, ' ')}: {value}
                      </span>
                    </div>
                  );
                }
                return null;
              })
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-border flex flex-wrap gap-3">
          {isDeveloper ? (
            <Button onClick={onUpgrade} className="flex-1">
              Upgrade to Paid Plan
            </Button>
          ) : (
            <>
              {subscription.status === 'active' && (
                <>
                  <Button onClick={onUpgrade} variant="secondary">
                    Change Plan
                  </Button>
                  <Button onClick={onManagePayment} variant="secondary" isLoading={isLoadingPortal}>
                    Manage Payment Method
                  </Button>
                  <Button onClick={onCancel} variant="secondary" className="text-destructive hover:text-destructive">
                    Cancel Subscription
                  </Button>
                </>
              )}
              {isPastDue && (
                <Button onClick={onManagePayment} isLoading={isLoadingPortal} className="flex-1">
                  Update Payment Method
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
